import { ArrowLeft, CheckCircle, Warning } from 'shared/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    CountServiceOrderItem,
    GetCountExpectation,
} from '../inventory/inventoryApi';
import BenchNumberPad from '../production/BenchNumberPad';
import { Mono } from '../../ui/Mono';
import Button from '../../ui/Button';
import {
    BigAction,
    CodeLine,
    FailureScreen,
    NoticeBanner,
    PlainAddress,
    ScanFlash,
    StepShell,
    SuccessScreen,
} from './CollectorChrome';
import ScanInput from './ScanInput';
import { CodesMatch, NormalizeScanCode, SCAN_METHOD_TYPED } from './collectorScan';
import { PlayMovementDone, PlayScanError, PlayScanSuccess } from './collectorFeedback';

/**
 * CONTAGEM NO COLETOR (M9 / §6.9) — a gramática, literal:
 *
 *     bipa a POSIÇÃO → bipa o(s) LPN(s) → confirma ou ajusta a QUANTIDADE
 *
 * ── Por que é um fluxo PRÓPRIO, e não o `ServiceOrderFlow` ──────────────────
 * O fluxo do M5 é um MOVIMENTO: origem → LPN → destino, e o bipe do destino é a
 * confirmação de que a carga chegou. Contar não move nada. Reaproveitar aquela
 * tela obrigaria a inventar um "destino" que não existe e ensinaria o operador
 * que contar é levar — que é justamente o engano que uma contagem não pode ter.
 *
 * O que É reusado: a moldura inteira do coletor (`StepShell`, `ScanInput`,
 * `CodeCard`, telas vermelha/verde, beep e vibração), o teclado numérico da
 * bancada do M6 e a mesma disciplina de bipe (esperado × lido; digitação só fora
 * do quiosque, gravando `TYPED` — no /coletor é só o bipe, ordem do dono de 26/09,
 * ver ScanInput).
 *
 * ── A REGRA QUE A TELA PRECISA MOSTRAR ──────────────────────────────────────
 * Divergência NÃO é erro do operador: é o resultado do trabalho dele. Por isso
 * ela aparece em ÂMBAR (a cor da espera do par no M5), nunca na tela vermelha —
 * vermelho é "você fez algo que o sistema não aceita", e contar 350 onde havia
 * 360 é exatamente o que a contagem existe para descobrir. E a rota SEGUE.
 */

const STEP_LOCATION = 'LOCATION';
const STEP_LPNS = 'LPNS';
const STEPS = [STEP_LOCATION, STEP_LPNS];

const SCREEN_STEP = 'STEP';
const SCREEN_MISMATCH = 'MISMATCH';
const SCREEN_FAILED = 'FAILED';
const SCREEN_RESULT = 'RESULT';
const SCREEN_DONE = 'DONE';

/** "12,5" (teclado brasileiro) → 12.5. Vazio conta como zero explícito. */
function toNumber(text) {
    const parsed = Number(String(text ?? '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
}

const FormatQty = (value) => new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 6 }).format(Number(value ?? 0));

const CountFlow = ({ label, operator, order, onExit }) => {
    const [items, setItems] = useState(order.items ?? []);
    const [step, setStep] = useState(STEP_LOCATION);
    const [screen, setScreen] = useState(SCREEN_STEP);
    const [expectation, setExpectation] = useState(null);
    // Leituras acumuladas nesta posição: `{[lpn_code]: {lpn_code, lines:[...]}}`.
    const [readings, setReadings] = useState({});
    const [activeLpn, setActiveLpn] = useState(null);
    const [qtyDraft, setQtyDraft] = useState({});
    const [typedInItem, setTypedInItem] = useState(false);
    const [failure, setFailure] = useState(null);
    const [result, setResult] = useState(null);
    const [flash, setFlash] = useState(null);
    const [busy, setBusy] = useState(false);

    const pending = useMemo(() => items.filter((entry) => entry.status === 'PENDING'), [items]);
    const item = pending[0] ?? null;
    const progress = useMemo(
        () => ({ position: items.length - pending.length + 1, total: items.length }),
        [items, pending],
    );

    const expectedCode = item?.to_location_code ?? null;

    const resetPosition = useCallback(() => {
        setStep(STEP_LOCATION);
        setScreen(SCREEN_STEP);
        setExpectation(null);
        setReadings({});
        setActiveLpn(null);
        setQtyDraft({});
        setTypedInItem(false);
        setFailure(null);
    }, []);

    useEffect(() => {
        if (!item && items.length > 0) {
            setScreen(SCREEN_DONE);
        }
    }, [item, items]);

    const fail = useCallback((payload) => {
        PlayScanError();
        setFailure(payload);
        setScreen(payload.screen ?? SCREEN_MISMATCH);
    }, []);

    /** O bipe da POSIÇÃO — igualdade exata com o endereço do item. */
    const scanLocation = useCallback(
        async (code, scanMethod) => {
            if (scanMethod === SCAN_METHOD_TYPED) {
                setTypedInItem(true);
            }
            if (expectedCode && !CodesMatch(expectedCode, code)) {
                fail({
                    title: label('wms.count.wrong_location_title'),
                    message: label('wms.count.step_location'),
                    expected: expectedCode,
                    read: NormalizeScanCode(code),
                });
                return;
            }
            PlayScanSuccess();
            setFlash(label('wms.collector.flash_origin'));
            setBusy(true);
            try {
                const payload = await GetCountExpectation(order.id, item.id);
                setExpectation(payload);
                setStep(STEP_LPNS);
            } catch (error) {
                fail({ screen: SCREEN_FAILED, title: label('wms.common.load_error'), message: error?.message ?? '' });
            } finally {
                setBusy(false);
            }
        },
        [expectedCode, fail, item, label, order.id],
    );

    /** O bipe do LPN — abre as linhas dele para confirmar ou ajustar. */
    const scanLpn = useCallback(
        (code, scanMethod) => {
            if (scanMethod === SCAN_METHOD_TYPED) {
                setTypedInItem(true);
            }
            const normalized = NormalizeScanCode(code);
            const known = (expectation?.lpns ?? []).find((lpn) => CodesMatch(lpn.code, normalized));
            PlayScanSuccess();
            setFlash(label('wms.collector.flash_lpn'));
            setActiveLpn(known ? known.code : normalized);
            if (known) {
                // Rascunho começa no ESPERADO: confirmar é o caso comum, e o
                // caso comum não pode custar digitação.
                setQtyDraft((current) => {
                    const next = { ...current };
                    for (const line of known.items) {
                        const key = `${known.code}|${line.id}`;
                        if (next[key] === undefined) {
                            next[key] = String(Number(line.qty)).replace('.', ',');
                        }
                    }
                    return next;
                });
            } else {
                // Pallet que o sistema não põe aqui: nada a confirmar — ele vira
                // incidente de ocupação não cadastrada no servidor (§6.2).
                setReadings((current) => ({ ...current, [normalized]: { lpn_code: normalized, lines: [] } }));
                setActiveLpn(null);
            }
        },
        [expectation, label],
    );

    const confirmLpn = useCallback(
        (lpn) => {
            setReadings((current) => ({
                ...current,
                [lpn.code]: {
                    lpn_code: lpn.code,
                    lines: lpn.items.map((line) => ({
                        lpn_item_id: line.id,
                        counted_qty: toNumber(qtyDraft[`${lpn.code}|${line.id}`]),
                    })),
                },
            }));
            setActiveLpn(null);
            PlayScanSuccess();
            setFlash(label('wms.count.counted_qty'));
        },
        [label, qtyDraft],
    );

    /** Fecha a POSIÇÃO: uma transação, com tudo que foi lido nela. */
    const finishPosition = useCallback(async () => {
        setBusy(true);
        try {
            const payload = await CountServiceOrderItem(order.id, item.id, {
                operator_code: operator?.code ?? null,
                // O pior caso da cadeia POR ITEM (mesma regra do M5): um item
                // digitado não contamina o seguinte.
                scan_method: typedInItem ? 'TYPED' : 'SCANNED',
                location_code: expectedCode,
                readings: Object.values(readings),
            });
            PlayMovementDone();
            setItems(payload.service_order?.items ?? items);
            setResult(payload);
            setScreen(SCREEN_RESULT);
        } catch (error) {
            fail({ screen: SCREEN_FAILED, title: label('wms.count.failed_title'), message: error?.message ?? '' });
        } finally {
            setBusy(false);
        }
    }, [expectedCode, fail, item, items, label, operator, order.id, readings, typedInItem]);

    const flashNode = flash ? <ScanFlash message={flash} onDone={() => setFlash(null)} /> : null;

    // ── Telas de exceção ────────────────────────────────────────────────────
    if (screen === SCREEN_MISMATCH || screen === SCREEN_FAILED) {
        return (
            <>
                {flashNode}
                <FailureScreen
                    label={label}
                    title={failure?.title}
                    message={failure?.message}
                    expected={failure?.expected}
                    read={failure?.read}
                    actions={
                        <>
                            <BigAction onClick={() => setScreen(SCREEN_STEP)}>
                                {label('wms.collector.try_again')}
                            </BigAction>
                            <Button variant="ghost" size="lg" className="w-full" onClick={onExit}>
                                {label('wms.collector.back_to_orders')}
                            </Button>
                        </>
                    }
                />
            </>
        );
    }

    // ── O RESULTADO da posição contada ──────────────────────────────────────
    if (screen === SCREEN_RESULT) {
        const count = result?.count ?? {};
        const diverged = count.summary?.diverged;
        return (
            <>
                {flashNode}
                <SuccessScreen
                    title={diverged ? label('wms.count.outcome.DIVERGENCE') : label('wms.count.outcome.MATCH')}
                    actions={
                        <BigAction
                            onClick={() => {
                                setResult(null);
                                resetPosition();
                            }}
                        >
                            {label('wms.count.next_position')}
                        </BigAction>
                    }
                >
                    <div className="space-y-3">
                        {/* ÂMBAR, não vermelho: divergência é o RESULTADO da
                            contagem, não um erro de quem contou. */}
                        {diverged ? (
                            <NoticeBanner tone="warning">{label('wms.count.divergence_hint')}</NoticeBanner>
                        ) : null}
                        {(count.lines ?? []).map((line, index) => (
                            <div
                                key={`${line.lpn_code}-${line.lpn_item_id ?? index}`}
                                className="rounded-xl border border-mono-200 px-3 py-2 text-left dark:border-mono-800"
                            >
                                <Mono className="text-sm font-bold">{line.lpn_code}</Mono>
                                <p className="text-sm text-mono-600 dark:text-mono-400">
                                    {label(`wms.count.outcome.${line.outcome}`)}
                                    {line.difference_qty && Number(line.difference_qty) !== 0
                                        ? ` · ${label('wms.count.expected_qty')} ${FormatQty(line.expected_qty)} → ${FormatQty(line.counted_qty)}`
                                        : ''}
                                </p>
                            </div>
                        ))}
                        <p className="text-sm text-mono-500">
                            {count.summary?.adjustment_count > 0
                                ? label('wms.count.adjusted')
                                : label('wms.count.not_adjusted')}
                        </p>
                    </div>
                </SuccessScreen>
            </>
        );
    }

    if (screen === SCREEN_DONE || !item) {
        return (
            <SuccessScreen
                title={label('wms.collector.order_done_title')}
                actions={
                    <BigAction onClick={onExit}>
                        <ArrowLeft size={22} className="mr-2" />
                        {label('wms.collector.back_to_orders')}
                    </BigAction>
                }
            >
                <Mono className="text-lg font-black">{order.code}</Mono>
            </SuccessScreen>
        );
    }

    // ── Os passos ───────────────────────────────────────────────────────────
    const stepIndex = Math.max(0, STEPS.indexOf(step));
    const stepName = `${label('wms.collector.item_counter')} ${progress.position} ${label('wms.collector.item_of')} ${progress.total} · ${
        step === STEP_LOCATION ? label('wms.count.step_location') : label('wms.count.step_lpn')
    }`;

    const activeLpnEntry = (expectation?.lpns ?? []).find((lpn) => lpn.code === activeLpn) ?? null;
    const allHandled =
        (expectation?.lpns ?? []).every((lpn) => readings[lpn.code]) && !activeLpnEntry;

    return (
        <>
            {flashNode}
            <StepShell
                stepIndex={stepIndex}
                stepCount={STEPS.length}
                stepName={stepName}
                footer={
                    <Button variant="ghost" size="lg" className="w-full" onClick={onExit} disabled={busy}>
                        <ArrowLeft size={20} className="mr-2" />
                        {label('wms.collector.back_to_orders')}
                    </Button>
                }
            >
                <div className="flex items-center justify-between gap-2 rounded-xl bg-mono-50 px-3 py-2 dark:bg-mono-900">
                    <Mono className="text-sm font-black text-black dark:text-white">{order.code}</Mono>
                    <span className="truncate text-xs text-mono-600 dark:text-mono-400">
                        {label('wms.count.title')}
                    </span>
                </div>

                {step === STEP_LOCATION ? (
                    <>
                        <h3 className="text-center text-xl font-bold text-mono-600 dark:text-mono-300">
                            {label('wms.count.step_location')}
                        </h3>
                        {/* Fala + código do VÃO a contar (navegação). O cartão
                            "Esperado" morreu: o bipe prova presença, a tela não
                            entrega a resposta. */}
                        <PlainAddress>
                            {(() => {
                                const partes = String(expectedCode || '').match(/^([A-Z]{1,2})-(\d{2})$/);
                                return partes ? `Rack ${partes[1]}, Bloco ${Number(partes[2])}` : expectedCode;
                            })()}
                        </PlainAddress>
                        <CodeLine>{expectedCode}</CodeLine>
                        <ScanInput
                            label={label}
                            onScan={scanLocation}
                            disabled={busy}
                            focusKey={`${item.id}-location`}
                        />
                    </>
                ) : null}

                {step === STEP_LPNS && !activeLpnEntry ? (
                    <>
                        <h3 className="text-center text-xl font-bold text-mono-600 dark:text-mono-300">
                            {label('wms.count.step_lpn')}
                        </h3>
                        <CodeLine>{expectedCode}</CodeLine>

                        {(expectation?.lpns ?? []).length === 0 ? (
                            <NoticeBanner tone="info">{label('wms.count.nothing_here')}</NoticeBanner>
                        ) : (
                            <div className="space-y-2">
                                <p className="text-xs uppercase tracking-wide text-mono-500">
                                    {label('wms.count.expected_here')}
                                </p>
                                {(expectation?.lpns ?? []).map((lpn) => (
                                    <div
                                        key={lpn.id}
                                        className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 ${
                                            readings[lpn.code]
                                                ? 'border-success-400 bg-success-soft dark:border-success-700 dark:bg-success-900/25'
                                                : 'border-mono-200 dark:border-mono-800'
                                        }`}
                                    >
                                        <Mono className="text-sm font-bold">{lpn.code}</Mono>
                                        {readings[lpn.code] ? (
                                            <CheckCircle
                                                size={22}
                                                weight="fill"
                                                className="text-success-text dark:text-success-400"
                                            />
                                        ) : (
                                            <span className="text-xs text-mono-500">{lpn.items.length} linha(s)</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <ScanInput
                            label={label}
                            onScan={scanLpn}
                            disabled={busy}
                            focusKey={`${item.id}-lpn-${Object.keys(readings).length}`}
                        />

                        {/* CONCLUIR sempre disponível: posição vazia é resposta
                            legítima, e pallet esperado que não apareceu vira
                            incidente no servidor — não uma tela que trava aqui. */}
                        <BigAction onClick={finishPosition} disabled={busy}>
                            {allHandled || (expectation?.lpns ?? []).length === 0 ? (
                                <CheckCircle size={22} className="mr-2" />
                            ) : (
                                <Warning size={22} className="mr-2" />
                            )}
                            {label('wms.count.finish_position')}
                        </BigAction>
                    </>
                ) : null}

                {step === STEP_LPNS && activeLpnEntry ? (
                    <>
                        <h3 className="text-center text-xl font-bold text-mono-600 dark:text-mono-300">
                            {label('wms.count.step_qty')}
                        </h3>
                        <Mono className="block text-center text-2xl font-black">{activeLpnEntry.code}</Mono>
                        {activeLpnEntry.items.map((line) => {
                            const key = `${activeLpnEntry.code}|${line.id}`;
                            const draft = qtyDraft[key] ?? '';
                            const difference = toNumber(draft) - Number(line.qty);
                            return (
                                <div key={line.id} className="space-y-2 rounded-xl border border-mono-200 p-3 dark:border-mono-800">
                                    <div className="flex items-center justify-between gap-2">
                                        <Mono className="text-sm font-bold">{line.sku || line.product_id}</Mono>
                                        {/* Contagem CEGA: a quantidade esperada NUNCA
                                            aparece antes do registro (regra do dono). */}
                                    </div>
                                    {line.supplier_lot ? (
                                        <p className="text-xs text-mono-500">{line.supplier_lot}</p>
                                    ) : null}
                                    <BenchNumberPad
                                        label={label('wms.count.counted_qty')}
                                        value={draft}
                                        onChange={(value) => setQtyDraft((current) => ({ ...current, [key]: value }))}
                                        hint={
                                            difference === 0
                                                ? label('wms.count.outcome.MATCH')
                                                : `${label('wms.count.difference')}: ${FormatQty(difference)}`
                                        }
                                    />
                                </div>
                            );
                        })}
                        <BigAction onClick={() => confirmLpn(activeLpnEntry)} disabled={busy}>
                            {label('wms.count.confirm_all')}
                        </BigAction>
                    </>
                ) : null}
            </StepShell>
        </>
    );
};

export default CountFlow;
